import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Redirect, RouteComponentProps} from 'react-router';
import styled from 'styled-components';

import FullscreenLoader from '../../components/fullscreenLoader';
import {pxToEm} from '../../components/globalStyles';
import HexagonIconImage from '../../components/hexagonIconImage';
import Layout from '../../components/layout';
import PageViewHeadline from '../../components/pageViewHeadline';
import SeeItInActionButton from '../../components/seeItInActionButton';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {SalesDemo, SalesDemoCategory, SalesDemoTag} from '../../logic/api/frontendService/entities';
import LaunchDashboardSalesDemosRegistry from '../../logic/launchDashboardSalesDemosRegistry';
import {AppRoutePathFunction} from '../../logic/routes';
import SalesDemoIconPathRegistry from '../../logic/salesDemoIconPathRegistry';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import StartDemoSvg from '../../media/hexagonIconImage/icons/start_demo.svg';

import IframeWrapper from './iframeWrapper';

//#region Styled Components

const StyledPartnerLogo = styled.img`
  background-color: white;
  padding: ${pxToEm(25)}em ${pxToEm(35)}em ${pxToEm(19)}em ${pxToEm(35)}em;
  border-radius: ${pxToEm(4)}em;
  width: ${pxToEm(220)}em;
  box-shadow: 0 0 5px rgba(51, 133, 227, 0.22);
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends RouteComponentProps<{demoId: string}>, MapStateToProps {}

class DemoDetailPageView extends Component<Props> {
  private handleBackButtonClick = () => {
    this.props.history.goBack();
  };

  public render() {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState === LoadingState.Loading) {
      return <FullscreenLoader />;
    }

    if (loadingState === LoadingState.Error || loadingState === LoadingState.Default) {
      return <Redirect to={AppRoutePathFunction.Root()} />;
    }

    const demos = response.demos || [];
    const foundDemo = demos.find(demo => demo.id === this.props.match.params.demoId);

    if (foundDemo === undefined) {
      return <Redirect to={AppRoutePathFunction.Root()} />;
    }

    const hasBetaTag = foundDemo.tags !== undefined && foundDemo.tags.some(tag => tag === SalesDemoTag.BETA);
    const hasPatentPendingTag =
      foundDemo.tags !== undefined && foundDemo.tags.some(tag => tag === SalesDemoTag.PATENT_PENDING);
    const hasPrototypeTag = foundDemo.tags !== undefined && foundDemo.tags.some(tag => tag === SalesDemoTag.PROTOTYPE);

    let partnerLogo: React.ReactNode;
    if (foundDemo.category === SalesDemoCategory.PARTNERS) {
      const partnerLogoIconPath = SalesDemoIconPathRegistry.getPartnerLogoIconPath(foundDemo.path);
      if (partnerLogoIconPath != undefined) {
        partnerLogo = <StyledPartnerLogo src={SalesDemoIconPathRegistry.getPartnerLogoIconPath(foundDemo.path)} />;
      }
    }

    return (
      <Layout>
        <PageViewHeadline
          topSubHeadline={foundDemo.name}
          headline={foundDemo.description}
          backButton={DemoDetailPageView.shouldShowBackButton(foundDemo)}
          onBackButtonClick={this.handleBackButtonClick}
          badgeComponent={<HexagonIconImage iconSrc={SalesDemoIconPathRegistry.getIconPath(foundDemo.path)} />}
          leftBadgeComponent={this.renderSeeItInActionButton(foundDemo)}
          rightBadgeComponent={partnerLogo}
          hasBetaTag={hasBetaTag}
          hasPatentPendingTag={hasPatentPendingTag}
          hasPrototypeTag={hasPrototypeTag}
        />

        <IframeWrapper key={foundDemo.id} baseUrl={response.baseUrl} salesDemo={foundDemo} />
      </Layout>
    );
  }

  private renderSeeItInActionButton = (demo: SalesDemo): React.ReactNode | null => {
    const url = LaunchDashboardSalesDemosRegistry.getLaunchDashboardLink(demo.path);
    if (url !== null) {
      return <SeeItInActionButton external={true} to={url} />;
    }

    if (demo.path === HardcodedSalesDemoPath.PerTitleEncoding) {
      const allLoadedDemos = this.props.salesDemos.response.demos || [];
      const foundDemo = allLoadedDemos.find(
        salesDemo => salesDemo.path === HardcodedSalesDemoPath.PerTitleEncodingDemo
      );
      if (foundDemo !== null) {
        return (
          <SeeItInActionButton
            to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}
            hexagonIconSrc={StartDemoSvg}
          />
        );
      }
    }

    return null;
  };

  private static shouldShowBackButton(salesDemo: SalesDemo): boolean {
    switch (salesDemo.path) {
      case HardcodedSalesDemoPath.CloudEncodingDemo:
      case HardcodedSalesDemoPath.CloudEncodingAws:
      case HardcodedSalesDemoPath.CloudEncodingGc:
      case HardcodedSalesDemoPath.OnPremiseEncodingDemo:
      case HardcodedSalesDemoPath.PerTitleEncodingDemo:
        return true;
      default:
        return false;
    }
  }
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(DemoDetailPageView);
